import React from 'react'
import { useDispatch } from 'react-redux'
import ModalActionButton from '../../elements/ModalActionButton'
import { ERROR, SET_SUCCESS } from '../../../store/types'
import { authHeader, baseUrl } from '../../../utils'
import axios from 'axios'
import TextField from '../../elements/form/TextField'
import NumberField from '../../elements/form/NumberField'
import { Switch } from '@headlessui/react'
import Checkbox from '../../elements/form/Checkbox';

const NewScoreGrade = ({close, reload}) => {
    const dispatch = useDispatch()

    const [gradePayload, setGradePayload] = React.useState({
        grade: '',
        minScore: 0,
        maxScore: '',
        classTypes: []
    })
    const [validationErrors, setValidationErrors] = React.useState({})

    const [processing, setProcessing] = React.useState(false)

    const validateForm = () => {
        let errors = {}
        if(!gradePayload.grade || gradePayload.grade === '') {
            errors.grade = true
        }
        if(gradePayload.minScore < 0 || gradePayload.minScore === '') {
            errors.minScore = true
        }
        if(!gradePayload.maxScore || gradePayload.maxScore === '') {
            errors.maxScore = true
        }
        if(gradePayload.classTypes.length === 0) {
            errors.classTypes = true
        }
        setValidationErrors(errors)

        return errors
    }

    const pushScoreGrade = async () => {
        try {
            if(processing) return
            const errors = validateForm()
            if(Object.values(errors).includes(true)) {
                return
            }
            setProcessing(true)

            const headers = authHeader()
            await axios.post(`${baseUrl}/academic-settings`, {scoreGrades: [gradePayload]}, { headers })
            dispatch({
                type: SET_SUCCESS,
                payload: 'Score grade created successfully'
            })
            reload()
            setTimeout(() => {
                close()
            }, 50);
        } catch (error) {
            console.log('error creating score grade: ', error)
            dispatch({
                type: ERROR,
                error
            })
        } finally {
            setProcessing(false)
        }
    }
    return (
        <div>
            <p className='text-sm text-gray-500'>Please provide details of the score grade in the fields below.</p>
            <div className='mt-4'>
                <TextField
                    inputLabel="Grade" 
                    fieldId="grade" 
                    inputType="text" 
                    inputPlaceholder="Enter grade (eg: A, B, C, etc.)"
                    requiredField={true}
                    preloadValue={gradePayload.grade || ''}
                    hasError={validationErrors && validationErrors.grade} 
                    returnFieldValue={(value)=>{setGradePayload({...gradePayload, ...{grade: value}})}}
                />
            </div>
            <div className='w-full my-2'>
                <NumberField
                    inputType="number" 
                    fieldId={`min-score`}
                    inputLabel="Min Score" 
                    requiredField={true}
                    preloadValue={gradePayload.minScore || ''}
                    hasError={validationErrors && validationErrors.minScore} 
                    returnFieldValue={(value)=>{setGradePayload({...gradePayload, ...{minScore: value}})}}
                    inputPlaceholder={`Minimum score required for this grade`}
                />
            </div>
            <div className='w-full my-2'>
                <NumberField
                    inputType="number" 
                    fieldId={`max-score`}
                    inputLabel="Max Score" 
                    requiredField={true}
                    preloadValue={gradePayload.maxScore || ''}
                    hasError={validationErrors && validationErrors.maxScore} 
                    returnFieldValue={(value)=>{setGradePayload({...gradePayload, ...{maxScore: value}})}}
                    inputPlaceholder={`Maximum score for this grade`}
                />
            </div>

            <div className='my-2'>
                <p className='text-sm text-gray-700 font-space-grotesk font-semibold'>Class Types</p>
                <p className='text-sm text-gray-500'>Select the class types that this score grade applies to.</p>
                {validationErrors.classTypes && <p className='text-xs text-red-500 my-2'>Please select at least one class type.</p>}
                <div className='w-full my-1'>
                    <Checkbox
                        CheckboxLabel="Junior Classes"
                        checkboxToggleFunction={()=>{
                            // toggleAccess('JUNIOR_CLASS')
                            if(gradePayload.classTypes?.includes('JUNIOR')){
                                setGradePayload({...gradePayload, classTypes: gradePayload.classTypes.filter(group => group !== 'JUNIOR')});
                            } else {
                                setGradePayload({...gradePayload, classTypes: [...(gradePayload.classTypes || []), 'JUNIOR']});
                            }
                        }} 
                        isChecked={gradePayload.classTypes?.includes('JUNIOR')} 
                        hasError={validationErrors && validationErrors.classTypes} 
                    />
                </div>
                <div className='w-full my-1'>
                    <Checkbox
                        CheckboxLabel="Senior Classes"
                        checkboxToggleFunction={()=>{
                            // toggleAccess('JUNIOR_CLASS')
                            if(gradePayload.classTypes?.includes('SENIOR')){
                                setGradePayload({...gradePayload, classTypes: gradePayload.classTypes.filter(group => group !== 'SENIOR')});
                            } else {
                                setGradePayload({...gradePayload, classTypes: [...(gradePayload.classTypes || []), 'SENIOR']});
                            }
                        }} 
                        isChecked={gradePayload.classTypes?.includes('SENIOR')} 
                        hasError={validationErrors && validationErrors.classTypes} 
                    />
                </div>
            </div>    
            
            <div className="w-full flex justify-between gap-x-4 mt-6 border-secondary border-opacity-50">
                <div className='w-10/12'>
                    <h3 className="block text-sm text-gray-700 font-space-grotesk font-semibold">Passing Grade</h3>
                    <p className="text-xs leading-tight text-gray-500 font-sofia-pro">If turned on, students with this score will be considered to have passed.</p>
                </div>
                <div className='w-2/12 flex flex-row-reverse pt-2'>
                    <Switch
                        checked={gradePayload.passingGrade}
                        onChange={()=>{setGradePayload({...gradePayload, passingGrade: !gradePayload.passingGrade})}}
                        className={`${
                            gradePayload.passingGrade ? 'bg-accent' : 'bg-gray-400'
                        } relative inline-flex items-center h-5 rounded-full w-10`}
                        >
                        <span className="sr-only">Passing grade</span>
                        <span
                            className={`transform transition ease-in-out duration-200 ${
                                gradePayload.passingGrade ? 'translate-x-6 bg-primary' : 'translate-x-1 bg-gray-600'
                            } inline-block w-3 h-3 transform rounded-full`}
                        />
                    </Switch>
                </div>
            </div>

            <div className="mt-8 flex flex-row-reverse gap-x-4">
                <ModalActionButton
                    buttonLabel={`Create Score Grade`}
                    buttonAction={()=>{pushScoreGrade()}}
                    processing={processing}
                />

                <button
                    type="button"
                    className="inline-flex justify-center px-5 py-4 text-xs font-medium bg-transparent border border-transparent rounded-md hover:bg-opacity-50 hover:bg-secondary focus:outline-none"
                    onClick={close}
                >
                    Cancel
                </button>
                
            </div>
        </div>
    )
}

export default NewScoreGrade